import React, { useEffect, useState } from 'react';
import { TextField, CnpjField, CheckboxField, SelectField, Field } from '../fundo_liquido/fields';
import { Opt, PAPEL } from '../fundo_liquido/mock';
import MultiSelect from '../../../components/Select/MultiSelect';
import './style.css';
import { useToast } from '../../../components/toast_notification/ToastProvider';
import {
  cadastrarPrestador, listarGruposEconomicos, cadastrarGrupoEconomico,
} from './prestadoresService';
import { PrestadorForm as TForm, GrupoEconomico, prestadorVazio } from './types';

const NOVO = '__novo__';
const soDigitos = (s?: string) => (s || '').replace(/\D/g, '');

// Seleciona um grupo econômico existente ou cadastra um novo na hora (CNPJ = PK).
const GrupoEconomicoField: React.FC<{
  cnpj?: string;
  onChange: (cnpj?: string, nome?: string) => void;
}> = ({ cnpj, onChange }) => {
  const toast = useToast();
  const [grupos, setGrupos] = useState<GrupoEconomico[]>([]);
  const [criando, setCriando] = useState(false);
  const [novoCnpj, setNovoCnpj] = useState('');
  const [novoNome, setNovoNome] = useState('');
  const [salvando, setSalvando] = useState(false);

  const carregar = () => { listarGruposEconomicos().then(setGrupos).catch(() => setGrupos([])); };
  useEffect(() => { carregar(); }, []);

  const options: Opt[] = [
    ...grupos.map((g) => ({ value: g.cnpj, label: g.nome })),
    { value: NOVO, label: '＋ Cadastrar novo grupo' },
  ];
  const selecionado = soDigitos(cnpj);

  const aoSelecionar = (v: string) => {
    if (v === NOVO) { setCriando(true); return; }
    onChange(v || undefined, grupos.find((g) => g.cnpj === v)?.nome);
  };

  const criar = async () => {
    if (!soDigitos(novoCnpj) || !novoNome.trim()) {
      toast.warning('Informe o CNPJ da empresa principal e o nome do grupo.');
      return;
    }
    setSalvando(true);
    try {
      const g = await cadastrarGrupoEconomico(novoCnpj, novoNome);
      setGrupos((prev) => (prev.some((x) => x.cnpj === g.cnpj) ? prev : [...prev, g]));
      onChange(g.cnpj, g.nome);
      setCriando(false); setNovoCnpj(''); setNovoNome('');
      toast.success(`Grupo "${g.nome}" criado.`);
    } catch (e: any) {
      toast.error(`Falha ao criar grupo: ${e?.message ?? 'erro'}`);
    } finally {
      setSalvando(false);
    }
  };

  if (criando) {
    return (
      <Field label="Novo grupo econômico" full hint="CNPJ da empresa principal do grupo (vira a chave do grupo).">
        <div className="fl-grid">
          <CnpjField label="CNPJ principal" value={novoCnpj} onChange={setNovoCnpj} />
          <TextField label="Nome do grupo" value={novoNome} onChange={setNovoNome} />
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button type="button" className="fl-btn fl-btn--primary" onClick={criar} disabled={salvando}>
            {salvando ? 'Criando...' : 'Criar grupo'}
          </button>
          <button type="button" className="fl-btn fl-btn--ghost" onClick={() => setCriando(false)} disabled={salvando}>
            Cancelar
          </button>
        </div>
      </Field>
    );
  }

  return (
    <SelectField
      label="Grupo econômico"
      value={selecionado}
      onChange={aoSelecionar}
      options={options}
      placeholder="Selecione ou cadastre..."
    />
  );
};

export const PrestadorCampos: React.FC<{
  form: TForm;
  onChange: (p: Partial<TForm>) => void;
  erros?: Record<string, string>;
}> = ({ form, onChange, erros = {} }) => (
  <div className="pf-form">
    <div className="fl-grid">
      <CnpjField label="CNPJ" value={form.cnpj} onChange={(v) => onChange({ cnpj: v })} error={erros.cnpj} />
      <TextField label="Nome" required value={form.nome} onChange={(v) => onChange({ nome: v })} error={erros.nome} />
      <GrupoEconomicoField
        cnpj={form.grupo_economico_cnpj}
        onChange={(cnpj, nome) => onChange({ grupo_economico_cnpj: cnpj, grupo_economico_nome: nome })}
      />
    </div>
    <div>
      <MultiSelect
        label="Papéis que pode exercer"
        values={form.papeis}
        onChange={(v) => onChange({ papeis: v })}
        options={PAPEL}
        placeholder="Selecione um ou mais papéis..."
      />
      <p className="pf-field-hint">Uma empresa pode exercer mais de um papel (ex.: Administradora e Custodiante).</p>
    </div>
    <div className="pf-anga-box">
      <CheckboxField label="É ANGA (empresa do grupo)" checked={form.is_anga} onChange={(v) => onChange({ is_anga: v })} />
      <p className="pf-anga-desc">Marque se o prestador é uma empresa do próprio grupo ANGA.</p>
    </div>
  </div>
);

const PrestadorCadastro: React.FC<{ onCriado?: (id: number) => void }> = ({ onCriado }) => {
  const toast = useToast();
  const [form, setForm] = useState<TForm>(prestadorVazio());
  const [erros, setErros] = useState<Record<string, string>>({});
  const [salvando, setSalvando] = useState(false);

  const patch = (p: Partial<TForm>) => setForm((prev) => ({ ...prev, ...p }));

  const validar = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.nome.trim()) e.nome = 'Informe o nome do prestador.';
    setErros(e);
    return Object.keys(e).length === 0;
  };

  const salvar = async () => {
    if (salvando) return;
    if (!validar()) return;
    setSalvando(true);
    try {
      const { prestador_id } = await cadastrarPrestador(form);
      toast.success(`Prestador "${form.nome}" cadastrado (#${prestador_id}).`);
      onCriado?.(prestador_id);
    } catch (e: any) {
      const det = e?.details ? ` — ${JSON.stringify(e.details)}` : '';
      toast.error(`Falha ao cadastrar: ${e?.message ?? 'erro'}${det}`);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div style={{ padding: '8px 0 24px', width: '100%' }}>
      <div className="fl-card">
        <PrestadorCampos form={form} onChange={patch} erros={erros} />
        <div className="pf-actions">
          <button className="fl-btn fl-btn--primary" onClick={salvar} disabled={salvando} type="button">
            {salvando ? 'Salvando...' : 'Cadastrar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrestadorCadastro;
